import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { DeviceService } from 'src/app/core/services/behavior/device.service';
import { LocalizarPedidoInputService } from 'src/app/core/services/behavior/localizar-pedido-input.service';
import { ModaisService } from 'src/app/core/services/behavior/modais.service';
import { NavbarService } from 'src/app/core/services/behavior/navbar.service';
import { StorageService } from 'src/app/core/services/behavior/storage.service';
import { DatetimeService } from 'src/app/core/services/http/datetime.service';

import { PedidoService } from 'src/app/core/services/http/pedido.service';
import { PagarContaComponent } from 'src/app/shared/components/pagar-conta/pagar-conta.component';
import { SelecionarProdutoComponent, SelecionarProdutoDataClose } from 'src/app/shared/components/selecionar-produto/selecionar-produto.component';
import { ModalRoleEnum } from 'src/app/shared/enums/modal-role.enum';
import { TipoAtendimentoEnum } from 'src/app/shared/enums/tipo-atendimento.enum';
import { ModalResult } from 'src/app/shared/interfaces/modal-result.interface';
import { FormaPgtoRequestV1 } from 'src/app/shared/models/forma-pgto.model';
import { PedidoBalcaoResponse, PedidoModel } from 'src/app/shared/models/pedido.model';
import { PessoaModel } from 'src/app/shared/models/pessoa.model';
import { UsuarioModel } from 'src/app/shared/models/usuario.model';
import { DialogStateUtil } from 'src/app/shared/utils/dialog-state.util';
import { HelpersUtil } from 'src/app/shared/utils/helpers.util';

@Component({
    selector: 'app-balcao',
    templateUrl: './balcao.component.html',
    styleUrls: ['./balcao.component.scss']
})
export class BalcaoComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('searchInpt', { static: false }) private searchInpt: ElementRef<HTMLInputElement>;

    private _podeCancelarPedido = false;
    private pedidos: PedidoBalcaoResponse[] = [];
    private subs$: Subscription[] = [];

    public isLoading = true;
    public pedidosFiltrados: PedidoBalcaoResponse[] = [];

    constructor(
        private _alertCtrl: AlertController,
        private modalCtrl: ModalController,
        private _localizarPedidoInputService: LocalizarPedidoInputService,
        private _navbarService: NavbarService,
        private pedidoService: PedidoService,
        private _storageService: StorageService,
        private _modaisService: ModaisService,
        private deviceService: DeviceService,
        private datetimeService: DatetimeService,
    ) { }

    public calcularTempo(tempo) {
        return HelpersUtil.formatarTempoDecorrido(tempo);
    }

    public async cancelarPedido(numeroComanda: number) {
        if (!this._podeCancelarPedido) {
            await this.showErrorMessage('Você não tem permissão para cancelar o pedido. Por favor, selecione um usuário que tenha autorização.');

            const confirmarUsuarioDialogDataClose = await this._modaisService.modalConfirmarUsuarioDialogAsync();

            if (confirmarUsuarioDialogDataClose.role === ModalRoleEnum.CANCEL)
                return;

            if (!confirmarUsuarioDialogDataClose.data.usuario.pdV_CancelarPedido)
                return await this.showErrorMessage('O usuário selecionado também não possui a permissão desejada.');
        }

        this.pedidoService.cancelarPedido(numeroComanda).subscribe(res => {
            if (res.success) {
                this.load();
            }
        })
    }

    public filtrarPedidos(value: string) {
        if (!value) {
            this.pedidosFiltrados = this.pedidos;
            return;
        }

        this.pedidosFiltrados = HelpersUtil.filterAllFields(this.pedidos, value);
    }

    public imprimir(pedidoBalcao: PedidoBalcaoResponse): void {
        const sub$ = this.pedidoService.imprimirPedido(pedidoBalcao.numeroComanda).subscribe();

        this.subs$.push(sub$);
    }

    public async modalPagarConta(pedidoBalcao: PedidoBalcaoResponse) {
        const pedido = new PedidoModel({
            numeroComanda: pedidoBalcao.numeroComanda,
            cliente: new PessoaModel({
                nome: pedidoBalcao.nomeCliente
            })
        });

        const modal = await this.modalCtrl.create({
            component: PagarContaComponent,
            componentProps: {
                pedido: pedido,
                tipoAtendimento: TipoAtendimentoEnum.BALCAO
            },
            cssClass: 'full-screen-dialog'
        });

        await modal.present();

        DialogStateUtil.dialogIsOpen();

        const modalResult: ModalResult<any> = await modal.onWillDismiss();

        DialogStateUtil.dialogIsClosed();

        if (modalResult.role === ModalRoleEnum.CONFIRM) {
            this.load();
        }
    }

    public async modalSelecionarProdutos(pedidoBalcao: PedidoBalcaoResponse = null) {
        let pedido = new PedidoModel();

        if (pedidoBalcao) {
            pedido = new PedidoModel({
                numeroComanda: pedidoBalcao.numeroComanda,
                cliente: new PessoaModel({
                    nome: pedidoBalcao.nomeCliente
                })
            });
        }

        const modal = await this.modalCtrl.create({
            component: SelecionarProdutoComponent,
            componentProps: {
                pedido: pedido,
                tipoAtendimento: TipoAtendimentoEnum.BALCAO
            },
            cssClass: 'full-screen-dialog',
            backdropDismiss: false,
            keyboardClose: false
        });

        await modal.present();

        DialogStateUtil.dialogIsOpen();

        const modalResult: ModalResult<SelecionarProdutoDataClose> = await modal.onWillDismiss();

        DialogStateUtil.dialogIsClosed();

        const formaPgto = new FormaPgtoRequestV1();
        modalResult.data.pedido.FormaPgtoOldV1 = formaPgto;
        modalResult.data.pedido.terminalId = this.deviceService.device.idDispositivo;

        if (modalResult.role === 'CONFIRM') {
            const datahora = await this.datetimeService.obterDataHora();
            modalResult.data.pedido.dataHora = datahora;

            this.pedidoService
                .enviarPedido(modalResult.data.pedido, TipoAtendimentoEnum.BALCAO)
                .subscribe(() => this.load());
        }
    }

    public ngAfterViewInit(): void {
        this.searchInpt.nativeElement.addEventListener('focus', () => this._localizarPedidoInputService.estaFocado = true);
        this.searchInpt.nativeElement.addEventListener('blur', () => this._localizarPedidoInputService.estaFocado = false);

        this._verificarMudancasNumeroComanda();
    }

    public ngOnDestroy(): void {
        this.subs$.forEach(sub$ => sub$.unsubscribe());
    }

    public async ngOnInit(): Promise<void> {
        this.load();

        const usuarioLogado = await this._storageService.get('user') as UsuarioModel;

        this._podeCancelarPedido = usuarioLogado.pdV_CancelarPedido;
    }

    @HostListener('window:keydown', ['$event'])
    public async onKeydown(event: KeyboardEvent): Promise<void> {
        const existeModalAberto = await this.modalCtrl.getTop();

        if (existeModalAberto) return;

        switch (event.key) {
            case 'F2':
                if (!DialogStateUtil.isDialogCurrentlyOpen)
                    this.modalSelecionarProdutos();

                break;
            case 'F8':
                this.searchInpt.nativeElement.focus();
                break;
        }
    }

    private _acionarFocoNumeroMesaInput() {
        HelpersUtil.executeAfterDelay(() => this._navbarService.focarNumeroMesaInput$.next(true));
    }

    private _verificarMudancasNumeroComanda() {
        this._navbarService.numeroComandaDigitado$.subscribe(res => {
            const numeroComanda = (res ?? '').toString();

            if (!numeroComanda) {
                return;
            }

            this.searchInpt.nativeElement.value = numeroComanda;
            this.searchInpt.nativeElement.focus();
            this.filtrarPedidos(numeroComanda);
        })
    }

    private load() {
        const sub$ = this.pedidoService
            .obterPedidosBalcao()
            .subscribe(res => {
                this.pedidos = res.success ? res.data : [];
                this.pedidosFiltrados = this.pedidos;
                this.isLoading = false;

                if (this._localizarPedidoInputService.existeValorParaSetar) {
                    this.searchInpt.nativeElement.value = this._localizarPedidoInputService.novoValorParaSetar;
                    this.searchInpt.nativeElement.focus();
                    this.filtrarPedidos(this.searchInpt.nativeElement.value);
                } else {
                    this._acionarFocoNumeroMesaInput();
                }
            });

        this.subs$.push(sub$);
    }

    private async showErrorMessage(msg: string) {
        const alert = await this._alertCtrl.create({
            header: 'ATENÇÃO',
            message: msg,
            buttons: ['OK']
        });

        await alert.present();

        await alert.onWillDismiss();
    }
}
